/**
 * Dispatcher chat panel (Feb 2026 v2).
 *
 * Adds vs. v1:
 *   • Date-day separators (Today / Yesterday / Feb 12) between message groups
 *   • WhatsApp-style ✓✓ read ticks on outgoing dispatcher messages
 *   • Live typing indicator via SSE ("Customer is typing…")
 *   • iOS-Glass audible chime on inbound customer message (with mute toggle)
 *   • Collapse/expand long messages (>240 chars)
 *   • Delete conversation button (dispatcher/SA only — hard-deletes thread + all messages)
 *   • SSE subscription for real-time message + read + thread_deleted events
 *     — the 5s active-thread poll is removed since SSE replaces it
 *
 * Backend endpoints used:
 *   GET     /api/admin/chat                      — thread list + total unread
 *   GET     /api/admin/chat/:cid                 — thread + messages
 *   POST    /api/admin/chat/:cid/messages        — send
 *   POST    /api/admin/chat/:cid/read            — mark read (publishes chat.read)
 *   POST    /api/admin/chat/:cid/typing          — publishes chat.typing
 *   DELETE  /api/admin/chat/:cid                 — hard-delete thread
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Send, MessageCircle, Search, Check, CheckCheck, Volume2, VolumeX, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useSSE } from '../hooks/useSSE';
import { playChime, isChatMuted, setChatMuted } from '../lib/chatSounds';
import { withDaySeparators, isLongMessage, formatShortTime } from '../lib/chatFormatting';
import { parseServerDate, formatDateET } from '../lib/timeET';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const POLL_MS = 15000; // thread-list poll (SSE handles active thread)
const TYPING_TIMEOUT_MS = 5000;
const TYPING_THROTTLE_MS = 3000;

function formatListTs(iso) {
  const d = parseServerDate(iso);
  if (!d) return '';
  const diffMin = (Date.now() - d.getTime()) / 60000;
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h`;
  return formatDateET(iso);
}

function MessageBody({ body }) {
  const [expanded, setExpanded] = useState(false);
  const long = isLongMessage(body);
  if (!long || expanded) {
    return (
      <>
        <p className="whitespace-pre-wrap break-words">{body}</p>
        {long && expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[10px] underline mt-1 opacity-70 hover:opacity-100"
            data-testid="dispatcher-chat-collapse"
          >
            Show less
          </button>
        )}
      </>
    );
  }
  const preview = body.slice(0, 220).replace(/\s+\S*$/, '') + '…';
  return (
    <>
      <p className="whitespace-pre-wrap break-words">{preview}</p>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-[10px] underline mt-1 opacity-70 hover:opacity-100"
        data-testid="dispatcher-chat-expand"
      >
        Show more
      </button>
    </>
  );
}

function Ticks({ status }) {
  // WhatsApp semantics:
  //   sending → single grey ✓ (optimistic, no id yet)
  //   sent    → double grey ✓✓ (delivered — server accepted it)
  //   read    → double blue ✓✓ (recipient opened the thread)
  const cls = status === 'read' ? 'text-[#3B9EFF]' : 'text-[#4B6B52]';
  const Icon = status === 'sending' ? Check : CheckCheck;
  return <Icon className={`w-3 h-3 inline-block ml-1 ${cls} ${status === 'sending' ? 'opacity-50' : ''}`} aria-label={`message ${status}`} />;
}

export default function DispatcherChatPanel({ initialCustomerId = null }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState([]);
  const [activeCustomerId, setActiveCustomerId] = useState(initialCustomerId);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [muted, setMuted] = useState(isChatMuted());
  const [typingAt, setTypingAt] = useState(0);
  const bottomRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const activeCidRef = useRef(activeCustomerId);
  useEffect(() => { activeCidRef.current = activeCustomerId; }, [activeCustomerId]);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/chat`, { withCredentials: true });
      setThreads(data.threads || []);
      setUnreadTotal(data.unread_total || 0);
    } catch {
      /* poll — swallow */
    }
  }, []);

  const loadActiveCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/chat/active-customers?limit=10`,
        { withCredentials: true },
      );
      setActive(data.active || []);
    } catch {
      /* silent — strip is a nice-to-have, never blocks the chat UI */
    }
  }, []);

  const loadThread = useCallback(async (cid, { markRead = true } = {}) => {
    if (!cid) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/chat/${cid}`, { withCredentials: true });
      setActiveThread(data.thread);
      setMessages(data.messages || []);
      if (markRead) {
        await axios.post(`${API_URL}/api/admin/chat/${cid}/read`, {}, { withCredentials: true });
        loadThreads();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load thread');
    }
  }, [loadThreads]);

  useEffect(() => {
    loadThreads();
    loadActiveCustomers();
    const t = setInterval(() => {
      loadThreads();
      loadActiveCustomers();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadThreads, loadActiveCustomers]);

  useEffect(() => {
    if (activeCustomerId) loadThread(activeCustomerId);
  }, [activeCustomerId, loadThread]);

  // Prod safety net: some CDN/proxy layers buffer SSE, so the live socket
  // can silently lag behind. Poll the active thread every 6 s in addition
  // to the SSE stream — dedup by message id means no duplicate rendering.
  // Skip mark-read on the poll (only mark on initial open + SSE inbound)
  // to avoid emitting a chat.read event every 6 s.
  useEffect(() => {
    if (!activeCustomerId) return undefined;
    const t = setInterval(() => {
      loadThread(activeCustomerId, { markRead: false });
    }, 6000);
    return () => clearInterval(t);
  }, [activeCustomerId, loadThread]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, typingAt]);

  // Auto-clear the typing indicator after TYPING_TIMEOUT_MS
  useEffect(() => {
    if (!typingAt) return undefined;
    const t = setTimeout(() => setTypingAt(0), TYPING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [typingAt]);

  // ---- SSE: real-time message/typing/read/thread_deleted events ----
  const handleSSE = useCallback((evt) => {
    if (!evt?.type?.startsWith('chat.')) return;
    const cid = activeCidRef.current;

    if (evt.type === 'chat.message') {
      // Always refresh the thread list so the sidebar reflects the new state.
      loadThreads();
      if (evt.customer_id !== cid || !evt.message) return;
      setMessages((prev) => (prev.some((m) => m.id === evt.message.id) ? prev : [...prev, evt.message]));
      if (evt.sender_role === 'customer') {
        playChime();
        // Auto-mark-read since dispatcher clearly has it open
        axios.post(`${API_URL}/api/admin/chat/${cid}/read`, {}, { withCredentials: true }).catch(() => {});
      }
    } else if (evt.type === 'chat.typing') {
      if (evt.customer_id === cid && evt.sender_role === 'customer') {
        setTypingAt(Date.now());
      }
    } else if (evt.type === 'chat.read') {
      // Customer opened the thread — flip our outgoing ticks to "read".
      if (evt.customer_id === cid && evt.viewer_role === 'customer') {
        const now = new Date().toISOString();
        setMessages((prev) => prev.map((m) => (m.sender_role !== 'customer' && !m.read_at ? { ...m, read_at: now } : m)));
      }
    } else if (evt.type === 'chat.thread_deleted') {
      if (evt.customer_id === cid) {
        setActiveCustomerId(null);
        setActiveThread(null);
        setMessages([]);
        toast.info('This conversation was deleted');
      }
      loadThreads();
    }
  }, [loadThreads]);
  useSSE(handleSSE, true);

  const notifyTyping = useCallback(() => {
    if (!activeCustomerId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = now;
    axios.post(`${API_URL}/api/admin/chat/${activeCustomerId}/typing`, {}, { withCredentials: true }).catch(() => {});
  }, [activeCustomerId]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending || !activeCustomerId) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/chat/${activeCustomerId}/messages`,
        { body },
        { withCredentials: true },
      );
      setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
      setDraft('');
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async () => {
    if (!activeCustomerId) return;
    const name = activeThread?.customer_name || activeThread?.customer_email || 'this customer';
    if (!window.confirm(`Delete the ENTIRE conversation with ${name}?\n\nAll messages will be permanently removed. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/chat/${activeCustomerId}`, { withCredentials: true });
      setActiveCustomerId(null);
      setActiveThread(null);
      setMessages([]);
      toast.success('Conversation deleted');
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    setChatMuted(m);
  };

  const filteredThreads = threads.filter((t) => {
    if (!filter) return true;
    const s = filter.toLowerCase();
    return (
      (t.customer_name || '').toLowerCase().includes(s) ||
      (t.customer_email || '').toLowerCase().includes(s) ||
      (t.last_message_snippet || '').toLowerCase().includes(s)
    );
  });

  const messageItems = useMemo(() => withDaySeparators(messages), [messages]);
  const showTyping = typingAt > 0 && Date.now() - typingAt < TYPING_TIMEOUT_MS;

  return (
    <div
      className="flex flex-col md:flex-row bg-[#0C0612] border border-[#2A0F3D] h-[calc(100dvh-16rem)] md:h-[70vh] min-h-[420px] md:min-h-[500px] mb-4 md:mb-0"
      data-testid="dispatcher-chat-panel"
    >
      {/* Left rail: thread list.
          Feb 2026 mobile fix — iMessage-style single-pane. On mobile the
          thread list is only shown when no conversation is active; when
          the user picks a thread, this pane hides so the conversation
          gets the full viewport width AND height (fixing the buried-input
          bug). Desktop keeps both panes side-by-side (`md:flex`). */}
      <div
        className={`${activeCustomerId ? 'hidden' : 'flex'} md:flex w-full md:w-72 border-b md:border-b-0 md:border-r border-[#2A0F3D] flex-col min-h-0`}
      >
        <div className="p-3 border-b border-[#2A0F3D]">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="w-4 h-4 text-[#39FF14]" />
            <p className="text-[#39FF14] font-mono text-sm font-bold uppercase">Chats</p>
            {unreadTotal > 0 && (
              <span className="ml-auto px-1.5 py-0.5 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[20px] text-center" data-testid="dispatcher-chat-unread-badge">
                {unreadTotal > 99 ? '99+' : unreadTotal}
              </span>
            )}
            <button
              type="button"
              onClick={toggleMute}
              className={`ml-1 p-1 border border-[#2A0F3D] ${muted ? 'text-[#4B6B52]' : 'text-[#39FF14]'} hover:border-[#39FF14]`}
              title={muted ? 'Unmute chime' : 'Mute chime'}
              data-testid="dispatcher-chat-mute-toggle"
              aria-label={muted ? 'Unmute chat sound' : 'Mute chat sound'}
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#4B6B52] absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="name / email / text"
              className="w-full bg-[#050003] border border-[#2A0F3D] pl-7 pr-2 py-1 text-[#39FF14] font-mono text-xs rounded-none focus:border-[#39FF14] focus:outline-none placeholder:text-[#4B6B52]"
              data-testid="dispatcher-chat-search"
            />
          </div>
        </div>

        {/* ACTIVE NOW strip — top 10 recently-active customers as
            one-tap chat shortcuts. Same data source as the Activity Feed.
            Clicking a chip opens an empty compose panel (thread is
            created on first send). */}
        {active.length > 0 && (
          <div
            className="border-b border-[#2A0F3D] px-3 py-2"
            data-testid="dispatcher-chat-active-strip"
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#4B6B52] mb-2 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
              Active now · {active.length}
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {active.map((c) => {
                const initials = (c.name || c.email || '?')
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((s) => s[0]?.toUpperCase() || '')
                  .join('') || '?';
                const isSelected = c.customer_id === activeCustomerId;
                return (
                  <button
                    key={c.customer_id}
                    type="button"
                    onClick={() => setActiveCustomerId(c.customer_id)}
                    className="flex flex-col items-center gap-1 min-w-[56px] max-w-[64px] focus:outline-none group"
                    title={`Start chat with ${c.name || c.email}`}
                    data-testid={`dispatcher-chat-active-${c.customer_id}`}
                  >
                    <span className="relative">
                      <span
                        className={`w-11 h-11 flex items-center justify-center font-mono text-xs font-bold border ${
                          isSelected
                            ? 'bg-[#39FF14] text-[#050003] border-[#39FF14]'
                            : 'bg-[#050003] text-[#39FF14] border-[#2A0F3D] group-hover:border-[#39FF14]'
                        }`}
                        style={{ borderRadius: '50%' }}
                      >
                        {initials}
                      </span>
                      {c.online && (
                        <span
                          className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-[#39FF14] border border-[#050003] rounded-full"
                          title="online now"
                        />
                      )}
                    </span>
                    <span className="text-[10px] font-mono text-[#A3C7A8] truncate w-full text-center">
                      {(c.name || c.email || '').split(' ')[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {filteredThreads.length === 0 && (
            <p className="text-[#4B6B52] font-mono text-xs text-center py-6">// no threads yet</p>
          )}
          {filteredThreads.map((t) => {
            const isActive = t.customer_id === activeCustomerId;
            const unread = t.unread_dispatcher_count || 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveCustomerId(t.customer_id)}
                className={`w-full text-left p-3 border-b border-[#2A0F3D] hover:bg-[#111A14] ${isActive ? 'bg-[rgba(57,255,20,0.05)] border-l-2 border-l-[#39FF14]' : ''}`}
                data-testid={`dispatcher-chat-thread-${t.customer_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className={`font-mono text-sm truncate ${unread > 0 ? 'text-[#39FF14] font-bold' : 'text-[#A3C7A8]'}`}>
                    {t.customer_name || t.customer_email || '(unknown)'}
                  </p>
                  <span className="text-[#4B6B52] font-mono text-[10px] flex-shrink-0">
                    {formatListTs(t.last_message_at)}
                  </span>
                </div>
                <p className="text-[#6B8E73] font-mono text-xs truncate mt-0.5">
                  {t.last_message_sender_role === 'customer' ? '' : '↩ '}
                  {t.last_message_snippet || '// no messages'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {t.region && <span className="text-[9px] text-[#4B6B52] font-mono uppercase">[{t.region}]</span>}
                  {unread > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 bg-[#FF3B30] text-white text-[9px] font-bold rounded-full">
                      {unread}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: active thread.
          Hidden on mobile when no thread is picked (see LEFT rail comment). */}
      <div className={`${activeCustomerId ? 'flex' : 'hidden'} md:flex flex-1 flex-col min-w-0 min-h-0`}>
        {!activeCustomerId ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#4B6B52] font-mono text-xs">// pick a thread from the left</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-[#2A0F3D] flex items-start justify-between gap-2">
              {/* Mobile-only back-to-list chevron. Desktop keeps both panes
                  visible so no back affordance is needed. */}
              <button
                type="button"
                onClick={() => { setActiveCustomerId(null); setActiveThread(null); }}
                className="md:hidden shrink-0 p-1.5 -ml-1 text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)]"
                aria-label="Back to threads"
                data-testid="dispatcher-chat-back-to-list"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[#39FF14] font-mono text-sm font-bold truncate" data-testid="dispatcher-chat-active-name">
                  {activeThread?.customer_name || activeThread?.customer_email || '(loading…)'}
                </p>
                <p className="text-[#4B6B52] font-mono text-[10px] truncate">
                  {activeThread?.customer_email} · {activeThread?.region?.toUpperCase() || '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={deleteConversation}
                className="flex items-center gap-1 border border-[#5A0F1F] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] hover:border-[#FF3B30] px-2 py-1 font-mono text-[10px] uppercase"
                title="Delete this conversation permanently"
                data-testid="dispatcher-chat-delete-thread"
              >
                <Trash2 className="w-3 h-3" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dispatcher-chat-messages">
              {messageItems.length === 0 && (
                <p className="text-[#4B6B52] font-mono text-xs text-center py-8">// no messages yet — say hi 👋</p>
              )}
              {messageItems.map((item) => {
                if (item.__sep) {
                  return (
                    <div key={item.id} className="flex items-center gap-2 my-4" data-testid={`dispatcher-chat-day-sep-${item.label}`}>
                      <div className="flex-1 h-px bg-[#2A0F3D]" />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#4B6B52] px-2">{item.label}</span>
                      <div className="flex-1 h-px bg-[#2A0F3D]" />
                    </div>
                  );
                }
                const m = item;
                // Track A — system messages render as a centered info
                // banner in the dispatcher panel too, so SA can see the
                // exact wording that landed in the customer's thread.
                if (m.sender_role === 'system') {
                  return (
                    <div key={m.id} className="flex justify-center my-2" data-testid={`dispatcher-chat-sys-${m.id}`}>
                      <div className="max-w-[85%] px-3 py-2 border border-[#2A0F3D] bg-[rgba(180,0,255,0.06)] font-mono text-xs text-[#E4CCFF] text-center">
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p className="text-[9px] mt-1 opacity-50">
                          {m.sender_name || 'System'} · {formatShortTime(m.created_at)}
                          {m.expires_at && ' · auto-clears after delivery'}
                        </p>
                      </div>
                    </div>
                  );
                }
                const dispatcher = m.sender_role !== 'customer';
                const tickStatus = m.read_at ? 'read' : 'sent';
                return (
                  <div key={m.id} className={`flex ${dispatcher ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-none border font-mono text-sm ${
                        dispatcher
                          ? 'bg-[rgba(180,0,255,0.08)] border-[#B400FF] text-[#E4CCFF]'
                          : 'bg-[#050003] border-[#2A0F3D] text-[#A3C7A8]'
                      }`}
                    >
                      {!dispatcher && <p className="text-[9px] text-[#4B6B52] mb-0.5">{m.sender_name || 'customer'}</p>}
                      <MessageBody body={m.body} />
                      <p className={`text-[9px] mt-1 opacity-60 flex items-center gap-1 ${dispatcher ? 'justify-end' : ''}`}>
                        {formatShortTime(m.created_at)}
                        {dispatcher && <Ticks status={tickStatus} />}
                      </p>
                    </div>
                  </div>
                );
              })}
              {showTyping && (
                <div className="flex justify-start" data-testid="dispatcher-chat-typing-indicator">
                  <div className="bg-[#050003] border border-[#2A0F3D] px-3 py-2 rounded-none">
                    <p className="text-[#A3C7A8] font-mono text-xs">
                      {activeThread?.customer_name || 'Customer'} is typing
                      <span className="inline-block animate-pulse ml-1">…</span>
                    </p>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-[#2A0F3D] flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Type your reply… (Enter to send · Shift+Enter for newline)"
                rows={2}
                maxLength={2000}
                className="flex-1 bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-sm p-2 rounded-none resize-none focus:border-[#39FF14] focus:outline-none placeholder:text-[#4B6B52]"
                data-testid="dispatcher-chat-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || !draft.trim()}
                className="bg-[#B400FF] text-white p-2 hover:bg-[#C42BFF] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="dispatcher-chat-send"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
